<script lang="ts">
import type { Temporal } from 'temporal-polyfill';
import type { DateTimeDisplayFormat, DateTimeDisplayMode } from '#layers/blocks/constants/date-time-display-options';
import type { DisplayBlockProps } from '#layers/blocks/types/display-block';
import { supportedDateFieldTypes } from '#layers/blocks/blocks/Input/InputDate.block.vue';
import {
	DATE_DISPLAY_FORMAT_OPTIONS,
	DATE_TIME_DISPLAY_FORMAT_VALUES,
	DATE_TIME_DISPLAY_MODE_OPTIONS,
	DATE_TIME_DISPLAY_MODE_VALUES,
} from '#layers/blocks/constants/date-time-display-options';

interface DisplayFormattedDateProps extends DisplayBlockProps<Temporal.PlainDate> {
	displayMode?: DateTimeDisplayMode;
	format?: DateTimeDisplayFormat;
}

const __DsBlock = {
	type: 'display',
	name: '$t:blocks.display_formatted_date.name',
	description: '$t:blocks.display_formatted_date.description',
	icon: 'lucide:calendar',
	category: 'display',
	hidden: true,

	fieldBinding: {
		supportedTypes: supportedDateFieldTypes,
	},

	options: ({ form, values }) => form([
		{
			name: 'value',
		},
		{
			name: 'displayMode',
			description: '$t:blocks.date_time_display_options.display_mode.description',
			block: '$builtin:InputSelect',
			type: 'enum',
			values: DATE_TIME_DISPLAY_MODE_VALUES,
			props: {
				options: DATE_TIME_DISPLAY_MODE_OPTIONS,
			},
		},
		...(values.displayMode === 'relative'
			? []
			: [{
					name: 'format',
					description: '$t:blocks.date_time_display_options.format.description',
					block: '$builtin:InputSelect',
					type: 'enum',
					values: DATE_TIME_DISPLAY_FORMAT_VALUES,
					props: {
						options: DATE_DISPLAY_FORMAT_OPTIONS,
					},
				}]),
	]),
}</script>

<script setup lang="ts">
import { computed } from 'vue';
import { formatPlainDate, formatRelativePlainDate, isNil, useUserLocale } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';

const { displayMode = 'absolute', format = 'medium', value } = defineProps<DisplayFormattedDateProps>();

;

const { formatLocale } = useUserLocale();

const formattedDate = computed(() => formatPlainDate(value, formatLocale.value, { dateStyle: format }));
const generalFormattedDate = computed(() => formatPlainDate(value, formatLocale.value));
const relativeDate = computed(() => value ? formatRelativePlainDate(value, { locale: formatLocale.value }) : undefined);
</script>

<template>
	<DDisplayValueEmpty v-if="isNil(value)" />
	<DTooltip v-else-if="displayMode === 'relative'" :delay-duration="0" :text="generalFormattedDate">
		<span>{{ relativeDate }}</span>
	</DTooltip>
	<div v-else>
		{{ formattedDate }}
	</div>
</template>
